import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Trans } from 'react-i18next';
import { DateInput } from '@/components/date-input';
import { Checkbox, ErrorBanner, Field, NativeSelect } from '@/components/form';
import { StatusChip } from '@/components/migration/file-drop';
import { ImportFixes } from '@/components/migration/import-fixes';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DrillLink } from '@/features/reports/report-table';
import type { Drill } from '@/features/reports/types';
import { useAccounts, useTaxCodes } from '@/lib/accounts';
import { api } from '@/lib/api';
import { formatDate, formatDateTime } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { fmtAmount, isZero, useMigration } from '@/lib/migration';
import { useOrgId } from '@/lib/org';
import {
  STATUS_TONE,
  checkBody,
  checkErrors,
  isActive,
  mappingBody,
  modeLabel,
  phaseLabel,
  progressOf,
  statusLabel,
  useImportRun,
  useImportRuns,
  useVerification,
  type CheckForm,
  type ImportPlan,
  type RunView,
} from '@/lib/qbo-history';
import { useQbo } from '@/lib/qbo';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/settings/migration/transactions')({
  validateSearch: (s: Record<string, unknown>): { run?: string } =>
    typeof s['run'] === 'string' ? { run: s['run'] } : {},
  component: ImportTransactions,
});

/**
 * Screen G12 (document 06): Import transactions from QuickBooks Online. Choose the range and
 * mode, check (dry run), map, start, watch the progress, then compare with QuickBooks.
 */
function ImportTransactions() {
  const orgId = useOrgId();
  const { run: runId } = Route.useSearch();
  const runs = useImportRuns();
  const qbo = useQbo();
  // the latest run unless one is chosen in the history
  const id = runId ?? runs.data?.find((r) => r.status !== 'cancelled')?.id;
  const t = useT();
  return (
    <>
      <PageHeader
        title={t('settingsPages.migration.history.importTransactions')}
        description={t('settingsPages.migrationTransactions.description')}
        actions={
          <Button variant="outline" asChild>
            <Link to="/settings/migration/history">
              {t('settingsPages.migration.history.title')}
            </Link>
          </Button>
        }
      />
      {!orgId ? (
        <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />
      ) : qbo.data && !qbo.data.connection ? (
        <EmptyState
          title={t('settingsPages.migrationTransactions.connectFirst')}
          hint={t('settingsPages.migrationTransactions.connectFirstHint')}
        />
      ) : runs.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : (
        <div className="flex max-w-6xl flex-col gap-4">
          <CheckCard key={orgId} />
          {id ? <RunPanel id={id} /> : null}
        </div>
      )}
    </>
  );
}

function CheckCard() {
  const t = useT();
  const orgId = useOrgId();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const m = useMigration();
  const conversion = m.data?.conversionDate ?? null;
  const [f, setF] = useState<CheckForm>({
    mode: 'full_history',
    from: '',
    to: '',
    estimates: true,
    allowExisting: false,
  });
  const [touched, setTouched] = useState(false);
  const errors = touched ? checkErrors(f) : {};
  const check = useMutation({
    mutationFn: () =>
      api<RunView>(`/orgs/${orgId}/qbo/history`, {
        method: 'POST',
        body: JSON.stringify(checkBody(f)),
      }),
    onSuccess: (v) => {
      void qc.invalidateQueries({ queryKey: ['qbo-history', orgId] });
      void navigate({ to: '/settings/migration/transactions', search: { run: v.id } });
    },
  });
  return (
    <section
      className="flex flex-col gap-3 rounded-lg border bg-card px-4 py-4"
      aria-labelledby="check-title"
      data-testid="qbo-import-check"
    >
      <h2 id="check-title" className="font-medium">
        {t('settingsPages.migrationTransactions.whatToImport')}
      </h2>
      <ErrorBanner>{check.error?.message}</ErrorBanner>
      <form
        className="grid gap-3 sm:grid-cols-4"
        onSubmit={(e) => {
          e.preventDefault();
          setTouched(true);
          if (Object.keys(checkErrors(f)).length === 0) check.mutate();
        }}
      >
        <Field
          label={t('settingsPages.migrationTransactions.import')}
          hint={
            f.mode === 'after_conversion'
              ? conversion
                ? t('settingsPages.migrationTransactions.fromConversion', {
                    date: formatDate(conversion),
                  })
                : t('settingsPages.migrationTransactions.postOpeningFirst')
              : t('settingsPages.migrationTransactions.everyTransaction')
          }
        >
          {(id) => (
            <NativeSelect
              id={id}
              value={f.mode}
              onChange={(e) => setF({ ...f, mode: e.target.value as CheckForm['mode'] })}
            >
              <option value="full_history">
                {t('settingsPages.migration.history.mode.full_history')}
              </option>
              <option value="after_conversion">
                {t('settingsPages.migration.history.mode.after_conversion')}
              </option>
            </NativeSelect>
          )}
        </Field>
        {f.mode === 'full_history' ? (
          <Field label={t('settingsPages.migrationTransactions.from')} error={errors.from}>
            {(id, d) => (
              <DateInput
                id={id}
                aria-describedby={d}
                value={f.from}
                invalid={Boolean(errors.from)}
                onChange={(from) => setF({ ...f, from })}
              />
            )}
          </Field>
        ) : (
          <div />
        )}
        <Field label={t('settingsPages.migrationTransactions.to')} error={errors.to}>
          {(id, d) => (
            <DateInput
              id={id}
              aria-describedby={d}
              value={f.to}
              invalid={Boolean(errors.to)}
              onChange={(to) => setF({ ...f, to })}
            />
          )}
        </Field>
        <div className="flex flex-col justify-end gap-2">
          <Checkbox
            label={t('settingsPages.migrationTransactions.estimatesAsQuotes')}
            checked={f.estimates}
            onChange={(e) => setF({ ...f, estimates: e.target.checked })}
          />
          <Checkbox
            label={t('settingsPages.migrationTransactions.allowExisting')}
            checked={f.allowExisting}
            onChange={(e) => setF({ ...f, allowExisting: e.target.checked })}
          />
        </div>
        <div className="sm:col-span-4">
          <Button type="submit" disabled={check.isPending}>
            {check.isPending
              ? t('settingsPages.migrationTransactions.checking')
              : t('settingsPages.migrationTransactions.check')}
          </Button>
          <span className="ms-3 text-xs text-muted-foreground">
            {t('settingsPages.migrationTransactions.nothingPosted')}
          </span>
        </div>
      </form>
    </section>
  );
}

function RunPanel({ id }: { id: string }) {
  const t = useT();
  const orgId = useOrgId();
  const qc = useQueryClient();
  const q = useImportRun(id);
  const act = useMutation({
    mutationFn: (what: 'start' | 'cancel' | 'resume') =>
      api<RunView>(`/orgs/${orgId}/qbo/history/${id}/${what}`, { method: 'POST' }),
    onSuccess: (v) => {
      qc.setQueryData(['qbo-history', orgId, id], v);
      void qc.invalidateQueries({ queryKey: ['qbo-history', orgId] });
    },
  });
  if (q.isPending) return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  if (q.isError) return <ErrorBanner>{q.error.message}</ErrorBanner>;
  const run = q.data;
  return (
    <section
      className="flex flex-col gap-4 rounded-lg border bg-card px-4 py-4"
      aria-labelledby="run-title"
      data-testid="qbo-import-run"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <h2 id="run-title" className="font-medium">
              {modeLabel(run.mode)}: {formatDate(run.from)} – {formatDate(run.to)}
            </h2>
            <StatusChip tone={STATUS_TONE[run.status]}>{statusLabel(run.status)}</StatusChip>
          </div>
          <p className="text-xs text-muted-foreground">
            {run.plan?.company.name ? `${run.plan.company.name} · ` : ''}
            {run.startedByName
              ? `${t('settingsPages.migrationTransactions.by', { name: run.startedByName })} · `
              : ''}
            {t('settingsPages.migrationTransactions.checked', {
              date: formatDateTime(run.createdAt),
            })}
            {run.finishedAt
              ? ` · ${t('settingsPages.migrationTransactions.finished', { date: formatDateTime(run.finishedAt) })}`
              : ''}
          </p>
        </div>
        <div className="flex gap-2">
          {run.status === 'planned' ? (
            <Button disabled={!run.canStart || act.isPending} onClick={() => act.mutate('start')}>
              {t('settingsPages.migrationTransactions.start')}
            </Button>
          ) : null}
          {run.status === 'failed' ? (
            <Button disabled={act.isPending} onClick={() => act.mutate('resume')}>
              {t('settingsPages.migrationTransactions.resume')}
            </Button>
          ) : null}
          {['planning', 'planned', 'queued', 'running', 'failed'].includes(run.status) ? (
            <Button
              variant="outline"
              disabled={act.isPending || run.cancelRequested}
              onClick={() => act.mutate('cancel')}
            >
              {run.cancelRequested
                ? t('settingsPages.migrationTransactions.cancelling')
                : t('common.cancel')}
            </Button>
          ) : null}
        </div>
      </div>
      <ErrorBanner>{act.error?.message}</ErrorBanner>
      {run.lastError ? <ErrorBanner>{run.lastError}</ErrorBanner> : null}
      {run.status === 'planning' ? (
        <p className="text-sm text-muted-foreground" role="status">
          {t('settingsPages.migrationTransactions.reading')}
        </p>
      ) : null}
      {isActive(run.status) && run.status !== 'planning' ? <Progress run={run} /> : null}
      {run.status === 'completed' ? <Result run={run} /> : null}
      {run.plan ? <Plan run={run} plan={run.plan} /> : null}
      {run.fixGroups.length ? <ImportFixes run={run} /> : null}
      {run.issues.length ? <Issues run={run} /> : null}
      {run.status === 'completed' ? <Verification id={run.id} /> : null}
    </section>
  );
}

function Progress({ run }: { run: RunView }) {
  const tr = useT();
  const phase = run.progress.phase ?? 'reading';
  const p = progressOf(run.progress.types);
  return (
    <div className="flex flex-col gap-2" role="status" aria-live="polite">
      <p className="text-sm">
        {phaseLabel(phase)}
        {run.progress.phase === 'importing'
          ? ` · ${tr('settingsPages.migrationTransactions.doneOf', { done: p.done, total: p.total })}`
          : ''}
      </p>
      <div className="h-2 w-full overflow-hidden rounded bg-muted">
        <div className="h-full bg-primary transition-all" style={{ width: `${p.percent}%` }} />
      </div>
      <ul className="grid gap-1 text-xs text-muted-foreground sm:grid-cols-3">
        {Object.entries(run.progress.types ?? {}).map(([type, t]) => (
          <li key={type}>
            {run.plan?.types.find((x) => x.type === type)?.label ?? type}:{' '}
            {t.done + t.skipped + t.failed}/{t.total}
            {t.failed
              ? ` · ${tr('settingsPages.migrationTransactions.failedCount', { n: t.failed })}`
              : ''}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Result({ run }: { run: RunView }) {
  const t = useT();
  return (
    <p className="text-sm" data-testid="qbo-import-result">
      <Trans
        t={t}
        i18nKey={
          run.failed
            ? 'settingsPages.migrationTransactions.resultFailed'
            : 'settingsPages.migrationTransactions.result'
        }
        values={{ imported: run.imported, skipped: run.skipped, failed: run.failed }}
        components={{ b: <strong />, bad: <strong className="text-destructive" /> }}
      />{' '}
      <Link
        className="underline"
        to="/settings/migration/history/$runId"
        params={{ runId: run.id }}
      >
        {t('settingsPages.migrationTransactions.seeImported')}
      </Link>
    </p>
  );
}

function Plan({ run, plan }: { run: RunView; plan: ImportPlan }) {
  const tr = useT();
  return (
    <div className="flex flex-col gap-4">
      {plan.blocking.length ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm"
          role="alert"
          data-testid="qbo-import-blocking"
        >
          <p className="font-medium">{tr('settingsPages.migrationTransactions.cannotStart')}</p>
          <ul className="ms-5 list-disc">
            {plan.blocking.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{tr('settingsPages.migration.openItems.col.type')}</TableHead>
            <TableHead className="text-end">
              {tr('settingsPages.migrationTransactions.col.inQuickBooks')}
            </TableHead>
            <TableHead className="text-end">
              {tr('settingsPages.migrationTransactions.col.toImport')}
            </TableHead>
            <TableHead className="text-end">
              {tr('settingsPages.migrationTransactions.col.alreadyImported')}
            </TableHead>
            <TableHead className="text-end">
              {tr('settingsPages.migration.history.col.skipped')}
            </TableHead>
            <TableHead className="text-end">
              {tr('settingsPages.migrationTransactions.col.problems')}
            </TableHead>
            <TableHead className="text-end">
              {tr('settingsPages.migration.openingBalances.total')}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {plan.types
            .filter((t) => t.count)
            .map((t) => (
              <TableRow key={t.type}>
                <TableCell>{t.label}</TableCell>
                <TableCell className="tnum text-end">{t.count}</TableCell>
                <TableCell className="tnum text-end">{t.toImport}</TableCell>
                <TableCell className="tnum text-end">{t.already}</TableCell>
                <TableCell className="tnum text-end">{t.skipped}</TableCell>
                <TableCell
                  className={cn('tnum text-end', t.errors && 'font-medium text-destructive')}
                >
                  {t.errors}
                </TableCell>
                <TableCell className="tnum text-end">{fmtAmount(t.total)}</TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      {plan.opening ? (
        <p className="text-sm">
          {tr('settingsPages.migrationTransactions.opening', {
            from: formatDate(run.from),
            lines: plan.opening.lines,
            asOf: formatDate(plan.opening.asOf),
            receivable: fmtAmount(plan.opening.receivable),
            payable: fmtAmount(plan.opening.payable),
          })}
        </p>
      ) : null}
      {run.status === 'planned' ? <Mapping run={run} plan={plan} /> : null}
    </div>
  );
}

function Mapping({ run, plan }: { run: RunView; plan: ImportPlan }) {
  const tr = useT();
  const orgId = useOrgId();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const accounts = useAccounts();
  // codes no longer in force too: older QuickBooks documents keep their rate (GST 12%, 28%)
  const codes = useTaxCodes({ includeInactive: true });
  const [acct, setAcct] = useState<Record<string, string>>({});
  const [tax, setTax] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: async () => {
      await api(`/orgs/${orgId}/qbo/history/mapping`, {
        method: 'PUT',
        body: JSON.stringify(mappingBody(plan.mapping, { accounts: acct, taxCodes: tax })),
      });
      // check again with the new mapping
      return api<RunView>(`/orgs/${orgId}/qbo/history`, {
        method: 'POST',
        body: JSON.stringify({
          mode: run.mode,
          ...(run.mode === 'full_history' ? { from: run.from } : {}),
          to: run.to,
          options: run.options,
        }),
      });
    },
    onSuccess: (v) => {
      toast(tr('settingsPages.migrationTransactions.mappingSaved'));
      void qc.invalidateQueries({ queryKey: ['qbo-history', orgId] });
      void navigate({ to: '/settings/migration/transactions', search: { run: v.id } });
    },
  });
  const toCreate = plan.mapping.contacts.filter((c) => c.how === 'create');
  const itemsToCreate = plan.mapping.items.filter((i) => i.how === 'create');
  const usable = (accounts.data ?? []).filter((a) => a.isActive);
  return (
    <details
      className="rounded-md border px-3 py-2"
      open={plan.blocking.length > 0}
      data-testid="qbo-import-mapping"
    >
      <summary className="cursor-pointer text-sm font-medium">
        {tr('settingsPages.migrationTransactions.mappingSummary', {
          accounts: plan.mapping.accounts.length,
          taxCodes: plan.mapping.taxCodes.length,
          contacts: toCreate.length,
          items: itemsToCreate.length,
        })}
      </summary>
      <div className="mt-3 flex flex-col gap-4">
        <ErrorBanner>{save.error?.message}</ErrorBanner>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{tr('settingsPages.migration.openingBalances.qbAccount')}</TableHead>
              <TableHead className="text-end">
                {tr('settingsPages.migrationTransactions.col.used')}
              </TableHead>
              <TableHead>{tr('settingsPages.migration.openingBalances.ledgerAccount')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {plan.mapping.accounts.map((a) => (
              <TableRow
                key={a.qboId}
                className={cn(!a.accountId && !acct[a.qboId] && 'bg-warning/10')}
              >
                <TableCell>
                  {a.name}
                  {a.type ? <div className="text-xs text-muted-foreground">{a.type}</div> : null}
                </TableCell>
                <TableCell className="tnum text-end">{a.used}</TableCell>
                <TableCell>
                  <NativeSelect
                    aria-label={tr('settingsPages.migrationTransactions.accountFor', {
                      name: a.name,
                    })}
                    value={acct[a.qboId] ?? a.accountId ?? ''}
                    onChange={(e) => setAcct({ ...acct, [a.qboId]: e.target.value })}
                  >
                    <option value="">{tr('settingsPages.migrationTransactions.notMapped')}</option>
                    {usable.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.code} {x.fullName}
                      </option>
                    ))}
                  </NativeSelect>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {plan.mapping.taxCodes.length ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{tr('settingsPages.migrationTransactions.col.qbTaxCode')}</TableHead>
                <TableHead>{tr('settingsPages.migrationTransactions.col.on')}</TableHead>
                <TableHead className="text-end">
                  {tr('settingsPages.migrationTransactions.col.rate')}
                </TableHead>
                <TableHead>{tr('settingsPages.migrationTransactions.col.ledgerTaxCode')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {plan.mapping.taxCodes.map((t) => {
                const k = `${t.side}:${t.qboId}`;
                return (
                  <TableRow key={k} className={cn(!t.taxCodeId && !tax[k] && 'bg-warning/10')}>
                    <TableCell>{t.name}</TableCell>
                    <TableCell>
                      {t.side === 'sales'
                        ? tr('settingsPages.migrationTransactions.sales')
                        : tr('settingsPages.migrationTransactions.purchases')}
                    </TableCell>
                    <TableCell className="tnum text-end">{t.rate}</TableCell>
                    <TableCell>
                      <NativeSelect
                        aria-label={
                          t.side === 'sales'
                            ? tr('settingsPages.migrationTransactions.taxCodeForSales', {
                                name: t.name,
                              })
                            : tr('settingsPages.migrationTransactions.taxCodeForPurchases', {
                                name: t.name,
                              })
                        }
                        value={tax[k] ?? t.taxCodeId ?? ''}
                        onChange={(e) => setTax({ ...tax, [k]: e.target.value })}
                      >
                        <option value="">
                          {tr('settingsPages.migrationTransactions.notMapped')}
                        </option>
                        {(codes.data ?? [])
                          .filter(
                            (c) => c.isActive && (c.appliesTo === 'both' || c.appliesTo === t.side),
                          )
                          .map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.code} · {c.name}
                              {c.effectiveTo
                                ? tr('settingsPages.migrationTransactions.untilDate', {
                                    date: formatDate(c.effectiveTo),
                                  })
                                : ''}
                            </option>
                          ))}
                      </NativeSelect>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : null}
        {toCreate.length || itemsToCreate.length ? (
          <p className="text-sm text-muted-foreground">
            {tr('settingsPages.migrationTransactions.addedOnStart', {
              list: [
                ...toCreate.map(
                  (c) =>
                    `${c.name} (${tr(`settingsPages.migrationTransactions.contactKind.${c.kind}`)}${c.currency !== plan.company.homeCurrency ? `${tr('settingsPages.migrationTransactions.listSeparator')}${c.currency}` : ''})`,
                ),
                ...itemsToCreate.map((i) => i.name),
              ].join(tr('settingsPages.migrationTransactions.listSeparator')),
            })}
          </p>
        ) : null}
        <div>
          <Button
            variant="outline"
            disabled={save.isPending || (!Object.keys(acct).length && !Object.keys(tax).length)}
            onClick={() => save.mutate()}
          >
            {save.isPending
              ? tr('common.saving')
              : tr('settingsPages.migrationTransactions.saveMapping')}
          </Button>
        </div>
      </div>
    </details>
  );
}

function Issues({ run }: { run: RunView }) {
  const t = useT();
  const errors = run.issues.filter((i) => i.severity === 'error');
  const warnings = run.issues.filter((i) => i.severity === 'warning');
  const open = (l: typeof errors) => l.filter((i) => !i.resolution).length;
  return (
    <details className="rounded-md border px-3 py-2" data-testid="qbo-import-issues">
      <summary className="cursor-pointer text-sm font-medium">
        {t('settingsPages.migrationTransactions.issuesSummary', {
          problems:
            open(errors) !== errors.length
              ? t('settingsPages.migrationTransactions.problemsOpen', {
                  count: errors.length,
                  open: open(errors),
                })
              : t('settingsPages.migrationTransactions.problems', { count: errors.length }),
          warnings:
            open(warnings) !== warnings.length
              ? t('settingsPages.migrationTransactions.warningsOpen', {
                  count: warnings.length,
                  open: open(warnings),
                })
              : t('settingsPages.migrationTransactions.warnings', { count: warnings.length }),
        })}
      </summary>
      <Table className="mt-2">
        <TableHeader>
          <TableRow>
            <TableHead />
            <TableHead>{t('settingsPages.migrationTransactions.col.transaction')}</TableHead>
            <TableHead>{t('settingsPages.migration.run.col.date')}</TableHead>
            <TableHead>{t('settingsPages.migrationTransactions.col.what')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...errors, ...warnings].map((i, n) => (
            <TableRow key={n}>
              <TableCell>
                {i.resolution ? (
                  <StatusChip tone="success">
                    {t(`settingsPages.migrationTransactions.resolution.${i.resolution}`)}
                  </StatusChip>
                ) : (
                  <StatusChip tone={i.severity === 'error' ? 'danger' : 'warning'}>
                    {i.severity === 'error'
                      ? t('settingsPages.migration.importFixes.notImported')
                      : t('settingsPages.migration.importFixes.note')}
                  </StatusChip>
                )}
              </TableCell>
              <TableCell className="text-sm">
                {i.type ? `${i.type} ${i.number ?? `#${i.qboId}`}` : '—'}
              </TableCell>
              <TableCell className="tnum">{i.date ? formatDate(i.date) : ''}</TableCell>
              <TableCell className="text-sm">{i.message}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </details>
  );
}

function Verification({ id }: { id: string }) {
  const t = useT();
  const orgId = useOrgId();
  const qc = useQueryClient();
  const v = useVerification(id, true);
  const refresh = useMutation({
    mutationFn: () => api(`/orgs/${orgId}/qbo/history/${id}/verification`, { method: 'POST' }),
    onSuccess: (data) => qc.setQueryData(['qbo-history', orgId, id, 'verification'], data),
  });
  if (v.isPending)
    return (
      <p className="text-sm text-muted-foreground">
        {t('settingsPages.migrationTransactions.loadingComparison')}
      </p>
    );
  if (v.isError) return <ErrorBanner>{v.error.message}</ErrorBanner>;
  const data = v.data;
  return (
    <div className="flex flex-col gap-3" data-testid="qbo-import-verification">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-medium">
          {t('settingsPages.migrationTransactions.compared')}{' '}
          {data.differences ? (
            <StatusChip tone="warning">
              {t('settingsPages.migrationTransactions.differences', { count: data.differences })}
            </StatusChip>
          ) : (
            <StatusChip tone="success">
              {t('settingsPages.migrationTransactions.noDifferences')}
            </StatusChip>
          )}
        </h3>
        <Button variant="outline" disabled={refresh.isPending} onClick={() => refresh.mutate()}>
          {refresh.isPending
            ? t('settingsPages.migrationTransactions.readingQuickBooks')
            : t('settingsPages.migrationTransactions.readAgain')}
        </Button>
      </div>
      <ErrorBanner>{refresh.error?.message}</ErrorBanner>
      {data.reports.map((r) => (
        <details
          key={`${r.report}-${r.asOf}`}
          open={r.differences > 0}
          className="rounded-md border px-3 py-2"
        >
          <summary className="cursor-pointer text-sm">
            {t('settingsPages.migrationTransactions.reportAt', {
              title: r.title,
              date: formatDate(r.asOf),
            })}
            {r.differences
              ? ` · ${t('settingsPages.migrationTransactions.differences', { count: r.differences })}`
              : ` · ${t('settingsPages.migrationTransactions.agrees')}`}
          </summary>
          <Table className="mt-2">
            <TableHeader>
              <TableRow>
                <TableHead />
                <TableHead className="text-end">
                  {t('settingsPages.migration.run.col.quickBooks')}
                </TableHead>
                <TableHead className="text-end">Outss Accounting</TableHead>
                <TableHead className="text-end">
                  {t('settingsPages.migration.openItems.difference')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {r.lines.map((l) => {
                const differs = !isZero(l.difference);
                return (
                  <TableRow
                    key={l.key}
                    className={cn(differs && 'bg-warning/10')}
                    data-difference={differs || undefined}
                  >
                    <TableCell>{l.label}</TableCell>
                    <TableCell className="tnum text-end">{fmtAmount(l.qbo)}</TableCell>
                    <TableCell className="tnum text-end">
                      {l.ledger !== null && l.drill ? (
                        <DrillLink drill={l.drill as Drill} amount>
                          {fmtAmount(l.ledger)}
                        </DrillLink>
                      ) : (
                        fmtAmount(l.ledger)
                      )}
                    </TableCell>
                    <TableCell className="tnum text-end">
                      {differs ? (
                        <strong>{fmtAmount(l.difference)}</strong>
                      ) : (
                        <span className="text-muted-foreground">{fmtAmount(l.difference)}</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </details>
      ))}
    </div>
  );
}
